import React, { createContext, useContext, useEffect, useState } from 'react';
import CookieConsent, { CookiePreferences } from './CookieConsent';

// Create the GDPR Context
interface GdprContextType {
    cookiePreferences: CookiePreferences;
    isConsentGiven: boolean;
    hasAnalyticsConsent: boolean;
    hasMarketingConsent: boolean;
    hasFunctionalConsent: boolean;
    updateCookiePreferences: (newPreferences: CookiePreferences) => void;
}

const GdprContext = createContext<GdprContextType>({
    cookiePreferences: {
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false,
    },
    isConsentGiven: false,
    hasAnalyticsConsent: false,
    hasMarketingConsent: false,
    hasFunctionalConsent: false,
    updateCookiePreferences: () => {},
});

// Custom hook to access GDPR context
export const useGdpr = () => useContext(GdprContext);

interface GdprProviderProps {
    children: React.ReactNode;
    privacyPolicyUrl: string;
    cookiePolicyUrl?: string;
    consentVersion: string;
}

const GdprProvider: React.FC<GdprProviderProps> = ({
    children,
    privacyPolicyUrl,
    cookiePolicyUrl,
    consentVersion,
}) => {
    const [cookiePreferences, setCookiePreferences] =
        useState<CookiePreferences>({
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false,
        });

    const [isConsentGiven, setIsConsentGiven] = useState<boolean>(false);

    useEffect(() => {
        // Check if consent has been given
        const cookieConsent = localStorage.getItem('cookieConsent');
        setIsConsentGiven(cookieConsent === 'true');

        // Load preferences from localStorage
        const storedPreferences = localStorage.getItem('cookiePreferences');
        if (storedPreferences) {
            try {
                setCookiePreferences(JSON.parse(storedPreferences));
            } catch (error) {
                console.error(
                    'Failed to parse stored cookie preferences',
                    error,
                );
            }
        }

        // Listen for cookie consent updates
        const handleCookieConsentUpdated = (
            event: CustomEvent<CookiePreferences>,
        ) => {
            setCookiePreferences(event.detail);
            setIsConsentGiven(true);
        };

        window.addEventListener(
            'cookieConsentUpdated',
            handleCookieConsentUpdated as EventListener,
        );

        return () => {
            window.removeEventListener(
                'cookieConsentUpdated',
                handleCookieConsentUpdated as EventListener,
            );
        };
    }, []);

    const updateCookiePreferences = (newPreferences: CookiePreferences) => {
        setCookiePreferences(newPreferences);
    };

    // Computed values for easier access
    const hasAnalyticsConsent = isConsentGiven && cookiePreferences.analytics;
    const hasMarketingConsent = isConsentGiven && cookiePreferences.marketing;
    const hasFunctionalConsent = isConsentGiven && cookiePreferences.functional;

    return (
        <GdprContext.Provider
            value={{
                cookiePreferences,
                isConsentGiven,
                hasAnalyticsConsent,
                hasMarketingConsent,
                hasFunctionalConsent,
                updateCookiePreferences,
            }}
        >
            {children}
            <CookieConsent
                privacyPolicyUrl={privacyPolicyUrl}
                cookiePolicyUrl={cookiePolicyUrl}
                consentVersion={consentVersion}
            />
        </GdprContext.Provider>
    );
};

export default GdprProvider;
